import {createRoot} from 'react-dom/client'
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {Board, Question} from "../javaTypes";
import QuestionAnswer from "./QuestionAnswer";
import ReferenceProvider from "./ReferenceProvider";
import SeasonLogo from "../SeasonLogo";
import {useCallback, useState} from "react";
import {messageDialog} from 'first-common/dialogs'
import {t} from "ttag";

declare global {
    interface Window {
        board: Board,
        isAnswerer: boolean,
        question?: Question,
        isFollowing?: boolean,
    }
}

function SingleQuestion({board, question, isFollowing: initialIsFollowing}: {board: Board, question: Question, isFollowing?: boolean}) {
    const [isFollowing, setIsFollowing] = useState(initialIsFollowing)
    const toggleFollow = useCallback(() => {
        setIsFollowing(prev => {
            const isRemoval = prev
            fetch(`/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}/qa/${question.number}/follow`, { method: isRemoval ? 'DELETE' : 'PUT' })
                .then(r => {
                    if(!r.ok) {
                        throw `Favorite update failed: ${r.status}`
                    }
                })
                .catch(ex => {
                    console.error(isRemoval ? "Failed to remove favorite" : "Failed to add favorite", ex)
                    messageDialog({
                        label: isRemoval ? t`Failed to remove favorite` : t`Failed to add favorite`,
                        text: <span dangerouslySetInnerHTML={{__html: t`An error occurred. Please try again later, or contact <i>FIRST</i> if this keeps occurring.`}} />
                    })
                    // Undo the mutation so frontend state matches backend
                    setIsFollowing((prev) => !prev);
                })
            return !prev
        });
    }, [setIsFollowing]);

    return <QuestionAnswer board={board} question={question} editing={false} canEditQuestion={false} canEditAnswer={false} isFollowing={isFollowing} toggleFollow={toggleFollow} />
}

const container = document.getElementById('reactContainer');
const queryClient = new QueryClient()
if(!container) {
    console.error("Mount point for runtime container not found")
} else {
    const root = createRoot(container);
    const board = window.board
    const baseUrl = `/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}`;
    root.render(<div>
        <div className={"text-center standardHeader"}>
            <div className={"d-flex justify-content-between align-items-center"}>
                <div style={{width: "1.5em"}}>&nbsp;</div>
                <SeasonLogo program={board.season.program} cmpYear={board.season.cmpYear} />
                <div style={{width: "1.5em"}}>{window.isAnswerer ? <a href={`${baseUrl}/answer/edit/${window.question!.number}`}>Edit</a> : null}</div>
            </div>
            <h2>{board.name} Q&A ({board.season.yearString})</h2>
        </div>
        <br/>
        <QueryClientProvider client={queryClient}>
            <ReferenceProvider board={window.board}>
                <SingleQuestion board={window.board} question={window.question!} isFollowing={window.isFollowing} />
            </ReferenceProvider>
        </QueryClientProvider>
    </div>);
}